import { axios } from '@vyce/core/src/api';
import { CardForm, BillingContact, BillingContactResponse, CardDetails } from '@vyce/core/src/types';

import { GetInvoicesPayload } from '../types';
import config from '../config';

// Every billing call goes through the gateway: it checks billing permissions for the company in the
// path, and only a subscription created here starts the trial emails.
const URL = `${config.gatewayApiUrl}/public/v1/billing`;

const prepareCardData = (data: CardForm): CardDetails => {
  const { expiry, number, ...rest } = data;
  const [exp_month, exp_year] = expiry.split('/');
  const fullYear = Number(`20${exp_year}`);

  return {
    exp_month: Number(exp_month),
    exp_year: fullYear,
    number: number.replace(/[\s]/g, ''),
    ...rest,
  };
};

export const createSubscriptionRequest = (token: string, companyId: string, data: CardForm) => {
  return axios.post(`${URL}/subscriptions/companies/${companyId}`, {
    bank_card: prepareCardData(data),
  });
};

export const resumeSubscriptionRequest = (token: string, companyId: string) => {
  return axios.post(`${URL}/subscriptions/companies/${companyId}/resume`);
};

export const cancelSubscriptionRequest = (token: string, companyId: string) => {
  return axios.delete(`${URL}/subscriptions/companies/${companyId}`);
};

export const getPaymentDetailsRequest = ({ token, companyId }: { token: string; companyId: string }) => {
  return axios.get(`${URL}/bank_cards/companies/${companyId}`, {
    params: { decrypted: true },
  });
};

export const getCardDetailsRequest = ({ token, cardId }: { token: string; cardId: string }) => {
  return axios.get(`${URL}/bank_cards/${cardId}`, {
    params: { decrypted: true },
  });
};

export const getBillingContactRequest = ({ token, companyId }: { token: string; companyId: string }) => {
  return axios.get(`${URL}/contacts/companies/${companyId}`);
};

export const updateBillingContactRequest = ({
  token,
  companyId,
  requestData,
}: {
  token: string;
  companyId: string;
  requestData: BillingContact;
}) => {
  return axios.put<BillingContactResponse>(`${URL}/contacts/companies/${companyId}`, requestData);
};

export const updateCardDetailsRequest = ({
  token,
  companyId,
  requestData,
}: {
  token: string;
  companyId: string;
  requestData: CardForm;
}) => {
  return axios.post<CardDetails>(
    `${URL}/subscriptions/companies/${companyId}/bank_cards`,
    prepareCardData(requestData)
  );
};

export const getInvoicesRequest = ({
  token,
  companyId,
  requestData,
}: {
  token: string;
  companyId: string;
  requestData: GetInvoicesPayload;
}) => {
  return axios.post(`${URL}/invoices/companies/${companyId}/search`, requestData);
};

export const getBillingDataRequest = ({ token, companyId }: { token: string; companyId: string }) => {
  return axios.get(`${URL}/invoices/companies/${companyId}/current_period`);
};

export const getSubscriptionDataRequest = ({ token, companyId }: { token: string; companyId: string }) => {
  return axios.get(`${URL}/subscriptions/companies/${companyId}`);
};

export const getSubscriptionStatusRequest = ({ token, companyId }: { token: string; companyId: string }) => {
  return axios.get(`${URL}/subscriptions/companies/${companyId}/is_subscribed`);
};

export const getMessagingSubscriptionRequest = (companyId: string) => {
  return axios.post(
    `/service/chats/companies/${companyId}/is_active`,
    {},
    {
      headers: {
        'service-token': config.serviceToken,
      },
    }
  );
};

export const addMessagingSubscriptionRequest = (companyId: string) => {
  return axios.post(`${URL}/subscriptions/companies/${companyId}/add_messaging_subscription_item`);
};

export const removeMessagingSubscriptionRequest = (companyId: string) => {
  return axios.post(`${URL}/subscriptions/companies/${companyId}/remove_messaging_subscription_item`);
};

export const getFormsSubscriptionRequest = (companyId: string) => {
  return axios.post(
    `/service/forms/companies/${companyId}/is_active`,
    {},
    {
      headers: {
        'service-token': config.serviceToken,
      },
    }
  );
};

export const addFormsSubscriptionRequest = (companyId: string) => {
  return axios.post(`${URL}/subscriptions/companies/${companyId}/add_forms_subscription_item`);
};

export const removeFormsSubscriptionRequest = (companyId: string) => {
  return axios.post(`${URL}/subscriptions/companies/${companyId}/remove_forms_subscription_item`);
};
